import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, RefreshCw, List, Columns, User, Clock,
  CheckCircle2, RotateCcw, MessageCircle, ChevronRight,
  Edit2, Trash2, X, Check, Paperclip,
} from 'lucide-react';
import { assetRequestsService, ASSET_REQUEST_STATUSES } from '../api/assetRequestsApi';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';
import { toast } from 'react-hot-toast';

// ── Meta ──────────────────────────────────────────────────────────────────────
const STATUS_META = {
  new:         { label: 'New',         bg: SYSTEM_COLORS.surfaceMuted,  color: SYSTEM_COLORS.textMuted  },
  in_progress: { label: 'In Progress', bg: SYSTEM_COLORS.infoTint,      color: SYSTEM_COLORS.info       },
  in_review:   { label: 'In Review',   bg: '#f3e8ff',                   color: '#7e22ce'                 },
  revisions:   { label: 'Revisions',   bg: SYSTEM_COLORS.warningTint,   color: SYSTEM_COLORS.warning    },
  approved:    { label: 'Approved',    bg: SYSTEM_COLORS.successTint,   color: SYSTEM_COLORS.success    },
  completed:   { label: 'Completed',   bg: '#d1fae5',                   color: '#065f46'                 },
  cancelled:   { label: 'Cancelled',   bg: SYSTEM_COLORS.surfaceMuted,  color: SYSTEM_COLORS.textSoft   },
};

const PRIORITY_META = {
  low:    { label: 'Low',    bg: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted },
  medium: { label: 'Medium', bg: SYSTEM_COLORS.infoTint,    color: SYSTEM_COLORS.info      },
  high:   { label: 'High',   bg: SYSTEM_COLORS.warningTint, color: SYSTEM_COLORS.warning   },
  urgent: { label: 'Urgent', bg: SYSTEM_COLORS.dangerTint,  color: SYSTEM_COLORS.danger    },
};

const REVIEW_ACTIONS = [
  { value: 'approve',         label: 'Approve',          Icon: CheckCircle2,  color: SYSTEM_COLORS.success },
  { value: 'request_changes', label: 'Request Revisions', Icon: RotateCcw,   color: SYSTEM_COLORS.warning  },
  { value: 'comment',         label: 'Comment only',      Icon: MessageCircle, color: SYSTEM_COLORS.info   },
];

const KANBAN_COLS = ['new', 'in_progress', 'in_review', 'revisions', 'approved', 'completed'];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const isOverdue = (d) => d && new Date(d) < new Date();

// ── Small helpers ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.new;
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap"
          style={{ backgroundColor: m.bg, color: m.color }}>
      {m.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const m = PRIORITY_META[priority] || PRIORITY_META.medium;
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap"
          style={{ backgroundColor: m.bg, color: m.color }}>
      {m.label}
    </span>
  );
};

const Avatar = ({ name }) => (
  <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white"
       style={{ backgroundColor: BRAND_COLORS.primary }}>
    {(name || '?').charAt(0).toUpperCase()}
  </div>
);

// ── Review log timeline ───────────────────────────────────────────────────────
const ReviewLogEntry = ({ entry }) => {
  const icons = {
    approve:         { Icon: CheckCircle2,  color: SYSTEM_COLORS.success },
    request_changes: { Icon: RotateCcw,    color: SYSTEM_COLORS.warning  },
    comment:         { Icon: MessageCircle, color: SYSTEM_COLORS.info    },
    assign:          { Icon: User,          color: BRAND_COLORS.primary  },
  };
  const { Icon, color } = icons[entry.action] || icons.comment;
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full"
           style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
        <Icon size={13} style={{ color }} />
      </div>
      <div className="min-w-0 pt-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold capitalize" style={{ color: NEUTRAL_COLORS.black }}>
            {(entry.action || '').replace(/_/g, ' ')}
          </span>
          {(entry.authorName || entry.authorId) && (
            <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
              {entry.authorName || entry.authorId}
            </span>
          )}
          <span className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>
            {fmtDate(entry.timestamp)}
          </span>
        </div>
        {entry.comment && (
          <p className="text-xs mt-0.5 leading-relaxed" style={{ color: SYSTEM_COLORS.textMuted }}>
            {entry.comment}
          </p>
        )}
      </div>
    </div>
  );
};

// ── Request Form Modal ────────────────────────────────────────────────────────
const EMPTY_FORM = {
  title: '', description: '', priority: 'medium', dueDate: '',
  assigneeId: '', projectId: '', contentId: '', promotionId: '',
  deliverableFormat: '', referenceTemplateIds: '', tags: '',
};

const RequestFormModal = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(
    initial
      ? {
          ...EMPTY_FORM, ...initial,
          dueDate:              initial.dueDate ? initial.dueDate.split('T')[0] : '',
          referenceTemplateIds: (initial.referenceTemplateIds || []).join(', '),
          tags:                 (initial.tags || []).join(', '),
        }
      : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        title:               form.title.trim(),
        description:         form.description.trim()       || undefined,
        priority:            form.priority,
        dueDate:             form.dueDate                  || undefined,
        assigneeId:          form.assigneeId.trim()        || undefined,
        projectId:           form.projectId.trim()         || undefined,
        contentId:           form.contentId.trim()         || undefined,
        promotionId:         form.promotionId.trim()       || undefined,
        deliverableFormat:   form.deliverableFormat.trim() || undefined,
        referenceTemplateIds: form.referenceTemplateIds
          ? form.referenceTemplateIds.split(',').map((s) => s.trim()).filter(Boolean)
          : undefined,
        tags: form.tags
          ? form.tags.split(',').map((s) => s.trim()).filter(Boolean)
          : undefined,
      };
      const saved = isEdit
        ? await assetRequestsService.update(initial.id, payload)
        : await assetRequestsService.create(payload);
      toast.success(isEdit ? 'Request updated' : 'Brief filed');
      onSaved(saved);
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const lbl = 'block text-xs font-semibold mb-1.5';
  const inp = 'w-full rounded-xl border px-3 py-2 text-sm outline-none';
  const is  = { borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: NEUTRAL_COLORS.black };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-6 overflow-y-auto"
         style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div className="relative w-full max-w-2xl rounded-2xl shadow-xl my-8"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>
        <div className="flex items-center justify-between px-6 py-4 border-b"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <h2 className="text-base font-bold" style={{ color: NEUTRAL_COLORS.black }}>
            {isEdit ? 'Edit Request' : 'New Design Brief'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X size={18} style={{ color: SYSTEM_COLORS.textMuted }} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Title *</label>
            <input type="text" value={form.title} onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. Event Poster for UDSM Open Day" className={inp} style={is} />
          </div>

          <div>
            <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Description / Brief</label>
            <textarea value={form.description} onChange={(e) => set('description', e.target.value)}
              rows={4} placeholder="Describe the design requirements, context, and goals…"
              className={inp} style={{ ...is, resize: 'vertical' }} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Priority</label>
              <select value={form.priority} onChange={(e) => set('priority', e.target.value)}
                className={inp} style={is}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Due Date</label>
              <input type="date" value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)}
                className={inp} style={is} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Deliverable Format</label>
              <input type="text" value={form.deliverableFormat}
                onChange={(e) => set('deliverableFormat', e.target.value)}
                placeholder="e.g. PNG 1920×1080, AI vector" className={inp} style={is} />
            </div>
            <div>
              <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Assign to (user ID)</label>
              <input type="text" value={form.assigneeId}
                onChange={(e) => set('assigneeId', e.target.value)}
                placeholder="Designer ID" className={inp} style={is} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[['projectId','Project ID'],['contentId','Content ID'],['promotionId','Promotion ID']].map(([k,l]) => (
              <div key={k}>
                <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>{l}</label>
                <input type="text" value={form[k]} onChange={(e) => set(k, e.target.value)}
                  placeholder="Optional" className={inp} style={is} />
              </div>
            ))}
          </div>

          <div>
            <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Tags (comma-separated)</label>
            <input type="text" value={form.tags} onChange={(e) => set('tags', e.target.value)}
              placeholder="poster, event, udsm" className={inp} style={is} />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-xl border"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-5 py-2 text-sm font-semibold rounded-xl text-white disabled:opacity-50"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'File Brief'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Detail Modal ──────────────────────────────────────────────────────────────
const DetailModal = ({ request: initReq, onClose, onUpdated, onDeleted, onEdit }) => {
  const [req,           setReq]           = useState(initReq);
  const [activeTab,     setActiveTab]     = useState('detail');
  const [assignId,      setAssignId]      = useState(initReq.assigneeId || '');
  const [reviewAction,  setReviewAction]  = useState('comment');
  const [reviewComment, setReviewComment] = useState('');
  const [deliverIds,    setDeliverIds]    = useState('');
  const [newStatus,     setNewStatus]     = useState(initReq.status);
  const [working,       setWorking]       = useState(false);
  const [deletingConf,  setDeletingConf]  = useState(false);

  const update = (updated) => {
    setReq(updated);
    setNewStatus(updated.status);
    onUpdated(updated);
  };

  const doAssign = async () => {
    if (!assignId.trim()) { toast.error('Enter assignee ID'); return; }
    setWorking(true);
    try { update(await assetRequestsService.assign(req.id, assignId.trim())); toast.success('Assigned'); }
    catch (e) { toast.error(e?.message || 'Failed'); }
    finally { setWorking(false); }
  };

  const doReview = async () => {
    setWorking(true);
    try {
      update(await assetRequestsService.review(req.id, {
        action: reviewAction,
        comment: reviewComment.trim() || undefined,
      }));
      toast.success('Review submitted');
      setReviewComment('');
    } catch (e) { toast.error(e?.message || 'Failed'); }
    finally { setWorking(false); }
  };

  const doStatus = async () => {
    setWorking(true);
    try { update(await assetRequestsService.changeStatus(req.id, newStatus)); toast.success('Status updated'); }
    catch (e) { toast.error(e?.message || 'Failed'); }
    finally { setWorking(false); }
  };

  const doDeliver = async () => {
    const ids = deliverIds.split(',').map((s) => s.trim()).filter(Boolean);
    if (!ids.length) { toast.error('Enter at least one asset ID'); return; }
    setWorking(true);
    try {
      update(await assetRequestsService.attachDeliverables(req.id, ids));
      toast.success('Deliverables attached');
      setDeliverIds('');
    } catch (e) { toast.error(e?.message || 'Failed'); }
    finally { setWorking(false); }
  };

  const doDelete = async () => {
    setWorking(true);
    try {
      await assetRequestsService.delete(req.id);
      toast.success('Deleted');
      onDeleted(req.id);
      onClose();
    } catch (e) { toast.error(e?.message || 'Failed'); setWorking(false); }
  };

  const inp = 'w-full rounded-xl border px-3 py-2 text-sm outline-none';
  const is  = { borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: NEUTRAL_COLORS.black };
  const sec = (t) => <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: SYSTEM_COLORS.textMuted }}>{t}</p>;

  const DTABS = [
    { id: 'detail',  label: 'Brief'                          },
    { id: 'actions', label: 'Actions'                        },
    { id: 'log',     label: `Log (${req.reviewLog?.length || 0})` },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-6 overflow-y-auto"
         style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div className="relative w-full max-w-2xl rounded-2xl shadow-xl my-8"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>
        {/* Header */}
        <div className="px-6 py-4 border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <PriorityBadge priority={req.priority} />
                <StatusBadge status={req.status} />
                {isOverdue(req.dueDate) && !['completed','cancelled'].includes(req.status) && (
                  <span className="text-xs font-semibold" style={{ color: SYSTEM_COLORS.danger }}>⚠ Overdue</span>
                )}
              </div>
              <h2 className="text-base font-bold leading-snug" style={{ color: NEUTRAL_COLORS.black }}>
                {req.title}
              </h2>
              <p className="text-xs mt-1" style={{ color: SYSTEM_COLORS.textMuted }}>
                {req.requestedBy && `Filed by ${req.requestedBy} · `}
                {fmtDate(req.createdAt)}
                {req.dueDate ? ` · Due ${fmtDate(req.dueDate)}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => { onClose(); onEdit(req); }}
                className="p-1.5 rounded-lg hover:bg-gray-100"
                title="Edit">
                <Edit2 size={15} style={{ color: SYSTEM_COLORS.textMuted }} />
              </button>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
                <X size={18} style={{ color: SYSTEM_COLORS.textMuted }} />
              </button>
            </div>
          </div>
          {/* Tab nav */}
          <div className="flex gap-1 mt-3">
            {DTABS.map(({ id, label }) => {
              const active = activeTab === id;
              return (
                <button key={id} onClick={() => setActiveTab(id)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                  style={{
                    backgroundColor: active ? BRAND_COLORS.primary : 'transparent',
                    color: active ? '#fff' : SYSTEM_COLORS.textMuted,
                  }}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* ── Brief ───────────────────────────────────────────────── */}
          {activeTab === 'detail' && (
            <>
              {req.description && (
                <div>
                  {sec('Brief')}
                  <p className="text-sm leading-relaxed whitespace-pre-wrap"
                     style={{ color: NEUTRAL_COLORS.black }}>{req.description}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                {req.deliverableFormat && (
                  <div>
                    {sec('Deliverable Format')}
                    <p className="text-sm" style={{ color: NEUTRAL_COLORS.black }}>{req.deliverableFormat}</p>
                  </div>
                )}
                {req.assigneeId && (
                  <div>
                    {sec('Assigned To')}
                    <div className="flex items-center gap-2">
                      <Avatar name={req.assigneeId} />
                      <span className="text-sm font-mono truncate" style={{ color: NEUTRAL_COLORS.black }}>
                        {req.assigneeId}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              {(req.projectId || req.contentId || req.promotionId) && (
                <div>
                  {sec('Linked To')}
                  <div className="flex flex-wrap gap-2">
                    {req.projectId   && <span className="text-xs rounded-full px-2.5 py-1" style={{ backgroundColor: SYSTEM_COLORS.infoTint,    color: SYSTEM_COLORS.info    }}>Project: {req.projectId}</span>}
                    {req.contentId   && <span className="text-xs rounded-full px-2.5 py-1" style={{ backgroundColor: '#f3e8ff',                  color: '#7e22ce'             }}>Content: {req.contentId}</span>}
                    {req.promotionId && <span className="text-xs rounded-full px-2.5 py-1" style={{ backgroundColor: SYSTEM_COLORS.warningTint, color: SYSTEM_COLORS.warning }}>Promo: {req.promotionId}</span>}
                  </div>
                </div>
              )}
              {req.deliverableAssetIds?.length > 0 && (
                <div>
                  {sec(`Deliverables (${req.deliverableAssetIds.length})`)}
                  <div className="flex flex-wrap gap-2">
                    {req.deliverableAssetIds.map((id) => (
                      <span key={id} className="text-xs font-mono rounded px-2 py-1"
                            style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted }}>
                        {id}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {req.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {req.tags.map((t) => (
                    <span key={t} className="text-xs rounded-full px-2.5 py-1"
                          style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textSoft }}>
                      #{t}
                    </span>
                  ))}
                </div>
              )}
              {!req.description && !req.deliverableFormat && !req.projectId && (
                <p className="text-sm text-center py-8" style={{ color: SYSTEM_COLORS.textMuted }}>
                  No brief details — click Actions to work with this request.
                </p>
              )}
            </>
          )}

          {/* ── Actions ─────────────────────────────────────────────── */}
          {activeTab === 'actions' && (
            <div className="space-y-4">
              {/* Assign */}
              <div className="rounded-xl border p-4 space-y-2"
                   style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                {sec('Assign to Designer')}
                <div className="flex gap-2">
                  <input type="text" value={assignId} onChange={(e) => setAssignId(e.target.value)}
                    placeholder="Designer user ID" className={`${inp} flex-1`} style={is} />
                  <button onClick={doAssign} disabled={working}
                    className="px-4 py-2 text-sm font-semibold rounded-xl text-white disabled:opacity-50 flex-shrink-0"
                    style={{ backgroundColor: BRAND_COLORS.primary }}>
                    Assign
                  </button>
                </div>
              </div>

              {/* Review */}
              <div className="rounded-xl border p-4 space-y-3"
                   style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                {sec('Submit Review')}
                <div className="flex gap-2 flex-wrap">
                  {REVIEW_ACTIONS.map(({ value, label, Icon, color }) => (
                    <button key={value} onClick={() => setReviewAction(value)}
                      className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold transition-colors"
                      style={{
                        borderColor:     reviewAction === value ? color : SYSTEM_COLORS.borderSoft,
                        backgroundColor: reviewAction === value ? `${color}22` : 'transparent',
                        color:           reviewAction === value ? color : SYSTEM_COLORS.textMuted,
                      }}>
                      <Icon size={12} /> {label}
                    </button>
                  ))}
                </div>
                <textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)}
                  rows={3} placeholder="Comment (optional for approve)…"
                  className={inp} style={{ ...is, resize: 'vertical' }} />
                <button onClick={doReview} disabled={working}
                  className="w-full py-2 text-sm font-semibold rounded-xl text-white disabled:opacity-50"
                  style={{ backgroundColor: BRAND_COLORS.primary }}>
                  {working ? 'Submitting…' : 'Submit Review'}
                </button>
              </div>

              {/* Status */}
              <div className="rounded-xl border p-4 space-y-2"
                   style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                {sec('Change Status')}
                <div className="flex gap-2">
                  <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}
                    className={`${inp} flex-1`} style={is}>
                    {ASSET_REQUEST_STATUSES.map((s) => (
                      <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>
                    ))}
                  </select>
                  <button onClick={doStatus} disabled={working || newStatus === req.status}
                    className="px-4 py-2 text-sm font-semibold rounded-xl text-white disabled:opacity-50 flex-shrink-0"
                    style={{ backgroundColor: BRAND_COLORS.primary }}>
                    Update
                  </button>
                </div>
              </div>

              {/* Attach deliverables */}
              <div className="rounded-xl border p-4 space-y-2"
                   style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                {sec('Attach Deliverables')}
                <div className="flex gap-2">
                  <input type="text" value={deliverIds} onChange={(e) => setDeliverIds(e.target.value)}
                    placeholder="Asset IDs, comma-separated" className={`${inp} flex-1`} style={is} />
                  <button onClick={doDeliver} disabled={working}
                    className="inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-xl text-white disabled:opacity-50 flex-shrink-0"
                    style={{ backgroundColor: BRAND_COLORS.primary }}>
                    <Paperclip size={13} /> Attach
                  </button>
                </div>
              </div>

              {/* Delete */}
              <div className="rounded-xl border p-4"
                   style={{ borderColor: SYSTEM_COLORS.dangerTint, backgroundColor: SYSTEM_COLORS.dangerTint }}>
                {sec('Danger Zone')}
                {deletingConf ? (
                  <div className="flex items-center gap-3">
                    <p className="flex-1 text-xs font-medium" style={{ color: SYSTEM_COLORS.danger }}>
                      Permanently delete this request?
                    </p>
                    <button onClick={doDelete} disabled={working}
                      className="px-3 py-1.5 text-xs font-semibold rounded-xl text-white disabled:opacity-50"
                      style={{ backgroundColor: SYSTEM_COLORS.danger }}>
                      {working ? 'Deleting…' : 'Yes, Delete'}
                    </button>
                    <button onClick={() => setDeletingConf(false)}
                      className="px-3 py-1.5 text-xs font-medium rounded-xl border"
                      style={{ borderColor: SYSTEM_COLORS.danger, color: SYSTEM_COLORS.danger }}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setDeletingConf(true)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold"
                    style={{ color: SYSTEM_COLORS.danger }}>
                    <Trash2 size={13} /> Delete this request
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Log ─────────────────────────────────────────────────── */}
          {activeTab === 'log' && (
            <div className="space-y-3">
              {!req.reviewLog?.length ? (
                <p className="text-sm text-center py-8" style={{ color: SYSTEM_COLORS.textMuted }}>
                  No review actions yet.
                </p>
              ) : (
                [...req.reviewLog].reverse().map((entry, i) => (
                  <ReviewLogEntry key={i} entry={entry} />
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Kanban card ───────────────────────────────────────────────────────────────
const RequestCard = ({ req, onClick }) => (
  <div className="rounded-xl border p-3 cursor-pointer space-y-2 hover:shadow-sm transition-shadow"
       style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
       onClick={onClick}>
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm font-semibold leading-tight line-clamp-2 flex-1"
         style={{ color: NEUTRAL_COLORS.black }}>
        {req.title}
      </p>
      <PriorityBadge priority={req.priority} />
    </div>
    {req.deliverableFormat && (
      <p className="text-xs truncate" style={{ color: SYSTEM_COLORS.textSoft }}>
        {req.deliverableFormat}
      </p>
    )}
    <div className="flex items-center justify-between">
      {req.dueDate ? (
        <span className="text-xs"
              style={{ color: isOverdue(req.dueDate) && !['completed','cancelled'].includes(req.status) ? SYSTEM_COLORS.danger : SYSTEM_COLORS.textMuted }}>
          {isOverdue(req.dueDate) && !['completed','cancelled'].includes(req.status) ? '⚠ ' : ''}
          {fmtDate(req.dueDate)}
        </span>
      ) : <span />}
      {req.assigneeId && <Avatar name={req.assigneeId} />}
    </div>
    {req.tags?.length > 0 && (
      <div className="flex flex-wrap gap-1">
        {req.tags.slice(0, 2).map((t) => (
          <span key={t} className="text-xs rounded-full px-2 py-0.5"
                style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textSoft }}>
            #{t}
          </span>
        ))}
        {req.tags.length > 2 && (
          <span className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>+{req.tags.length - 2}</span>
        )}
      </div>
    )}
  </div>
);

// ── Kanban view ───────────────────────────────────────────────────────────────
const KanbanView = ({ requests, onCardClick }) => (
  <div className="flex gap-4 overflow-x-auto pb-4">
    {KANBAN_COLS.map((status) => {
      const m   = STATUS_META[status];
      const col = requests.filter((r) => r.status === status);
      return (
        <div key={status} className="flex-shrink-0 w-52 space-y-2">
          <div className="flex items-center justify-between px-1 mb-1">
            <span className="text-xs font-bold uppercase tracking-wide"
                  style={{ color: NEUTRAL_COLORS.black }}>{m.label}</span>
            <span className="text-xs font-semibold rounded-full px-2 py-0.5"
                  style={{ backgroundColor: m.bg, color: m.color }}>
              {col.length}
            </span>
          </div>
          {col.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed h-16 flex items-center justify-center"
                 style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
              <span className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>—</span>
            </div>
          ) : (
            col.map((r) => <RequestCard key={r.id} req={r} onClick={() => onCardClick(r)} />)
          )}
        </div>
      );
    })}
  </div>
);

// ── List view ─────────────────────────────────────────────────────────────────
const ListView = ({ requests, onCardClick }) => (
  <div className="overflow-x-auto rounded-xl border"
       style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
    <table className="min-w-full">
      <thead style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
        <tr>
          {['Brief', 'Priority', 'Status', 'Due Date', 'Assignee', 'Updated'].map((h) => (
            <th key={h}
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                style={{ color: SYSTEM_COLORS.textMuted }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
        {requests.length === 0 ? (
          <tr>
            <td colSpan={6} className="px-4 py-12 text-center text-sm"
                style={{ color: SYSTEM_COLORS.textMuted }}>
              No requests found
            </td>
          </tr>
        ) : (
          requests.map((req) => (
            <tr key={req.id}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => onCardClick(req)}>
              <td className="px-4 py-3 max-w-xs">
                <p className="text-sm font-semibold truncate" style={{ color: NEUTRAL_COLORS.black }}>
                  {req.title}
                </p>
                {req.deliverableFormat && (
                  <p className="text-xs truncate mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
                    {req.deliverableFormat}
                  </p>
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <PriorityBadge priority={req.priority} />
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <StatusBadge status={req.status} />
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm"
                  style={{
                    color: isOverdue(req.dueDate) && !['completed','cancelled'].includes(req.status)
                      ? SYSTEM_COLORS.danger
                      : SYSTEM_COLORS.textMuted,
                  }}>
                {fmtDate(req.dueDate)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                {req.assigneeId ? (
                  <div className="flex items-center gap-1.5">
                    <Avatar name={req.assigneeId} />
                    <span className="text-xs truncate max-w-[80px]" style={{ color: SYSTEM_COLORS.textMuted }}>
                      {req.assigneeId}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>Unassigned</span>
                )}
              </td>
              <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: SYSTEM_COLORS.textMuted }}>
                {fmtDate(req.updatedAt)}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// ── AssetRequests ─────────────────────────────────────────────────────────────
const AssetRequests = () => {
  const [requests,     setRequests]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [viewMode,     setViewMode]     = useState('list');
  const [myOnly,       setMyOnly]       = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [detail,       setDetail]       = useState(null);
  const [formModal,    setFormModal]    = useState(null); // null | 'new' | request

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = myOnly
        ? await assetRequestsService.getMe()
        : await assetRequestsService.list(statusFilter ? { status: [statusFilter] } : {});
      setRequests(data || []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [myOnly, statusFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleSaved = (saved) => {
    setRequests((prev) => {
      const idx = prev.findIndex((r) => r.id === saved.id);
      return idx >= 0 ? prev.map((r, i) => (i === idx ? saved : r)) : [saved, ...prev];
    });
  };

  const handleUpdated = (updated) => {
    setRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setDetail(updated);
  };

  const handleDeleted = (id) => {
    setRequests((prev) => prev.filter((r) => r.id !== id));
  };

  // KPI
  const counts = ASSET_REQUEST_STATUSES.reduce((acc, s) => {
    acc[s] = requests.filter((r) => r.status === s).length;
    return acc;
  }, {});

  const KPI_ITEMS = [
    { label: 'Total',       value: requests.length,     color: NEUTRAL_COLORS.black  },
    { label: 'New',         value: counts.new,          color: SYSTEM_COLORS.textMuted },
    { label: 'In Progress', value: counts.in_progress,  color: SYSTEM_COLORS.info    },
    { label: 'In Review',   value: counts.in_review,    color: '#7e22ce'              },
    { label: 'Approved',    value: counts.approved,     color: SYSTEM_COLORS.success },
    { label: 'Completed',   value: counts.completed,    color: '#065f46'              },
  ];

  return (
    <div className="space-y-5">
      {/* ── Top bar ──────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* KPI chips */}
        <div className="flex items-center gap-2 flex-wrap">
          {KPI_ITEMS.map(({ label, value, color }) => (
            <div key={label}
                 className="flex items-center gap-1.5 px-3 py-2 rounded-xl border"
                 style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
              <span className="text-base font-bold" style={{ color }}>{value}</span>
              <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>{label}</span>
            </div>
          ))}
        </div>
        {/* Controls */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={fetchRequests}
            className="inline-flex items-center gap-1 rounded-xl border px-3 py-2 text-xs font-medium"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted, backgroundColor: SYSTEM_COLORS.shellSurface }}>
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
          <div className="flex rounded-xl overflow-hidden border" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
            {[{ id: 'list', Icon: List }, { id: 'kanban', Icon: Columns }].map(({ id, Icon }) => (
              <button key={id} onClick={() => setViewMode(id)} className="px-3 py-2"
                style={{
                  backgroundColor: viewMode === id ? BRAND_COLORS.primary : SYSTEM_COLORS.shellSurface,
                  color: viewMode === id ? '#fff' : SYSTEM_COLORS.textMuted,
                }}>
                <Icon size={14} />
              </button>
            ))}
          </div>
          <button onClick={() => setFormModal('new')}
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold text-white"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            <Plus size={13} /> New Brief
          </button>
        </div>
      </div>

      {/* ── Filters ──────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {[null, ...ASSET_REQUEST_STATUSES.filter(s => s !== 'cancelled')].map((s) => {
            const active = statusFilter === s;
            const meta   = s ? STATUS_META[s] : null;
            return (
              <button key={s ?? 'all'}
                onClick={() => setStatusFilter(s === statusFilter ? null : s)}
                className="flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors whitespace-nowrap"
                style={{
                  backgroundColor: active ? BRAND_COLORS.primary : SYSTEM_COLORS.shellSurface,
                  color: active ? '#fff' : SYSTEM_COLORS.textMuted,
                  border: `1px solid ${active ? 'transparent' : SYSTEM_COLORS.borderSoft}`,
                }}>
                {s ? `${meta.label} (${counts[s]})` : 'All'}
              </button>
            );
          })}
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none flex-shrink-0">
          <div onClick={() => setMyOnly((v) => !v)}
            className="relative rounded-full transition-colors"
            style={{ backgroundColor: myOnly ? BRAND_COLORS.primary : SYSTEM_COLORS.surfaceMuted, width: '32px', height: '18px' }}>
            <div className="absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow transition-all"
                 style={{ left: myOnly ? '15px' : '2px' }} />
          </div>
          <span className="text-xs font-medium" style={{ color: NEUTRAL_COLORS.black }}>My requests</span>
        </label>
      </div>

      {/* ── Content ──────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center h-48 rounded-xl border"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <div className="h-8 w-8 animate-spin rounded-full border-b-2"
               style={{ borderColor: BRAND_COLORS.primary }} />
        </div>
      ) : viewMode === 'kanban' ? (
        <KanbanView requests={requests} onCardClick={setDetail} />
      ) : (
        <ListView requests={requests} onCardClick={setDetail} />
      )}

      {/* ── Modals ──────────────────────────────────────────────── */}
      {formModal && (
        <RequestFormModal
          initial={formModal === 'new' ? null : formModal}
          onClose={() => setFormModal(null)}
          onSaved={(saved) => { handleSaved(saved); setFormModal(null); }}
        />
      )}
      {detail && (
        <DetailModal
          request={detail}
          onClose={() => setDetail(null)}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
          onEdit={(req) => setFormModal(req)}
        />
      )}
    </div>
  );
};

export default AssetRequests;
